/* eslint-disable */
/**
 * DataTabel — Sortérbar, filtrerbar tabel med tom-tilstand og pagination
 *
 * Props:
 *   kolonner    Array<{ key, label, bredde?, align?, render? }>
 *               render(value, row) → ReactNode
 *   rækker      Array<object>
 *   tomTekst    string   — vises når rækker er tomme
 *   tomBesked   string
 *   tomIkon     string
 *   pageSize    number   — antal rækker per side (default 10, 0 = ingen pagination)
 *   style       obj
 *   loading     bool
 */

import React, { useState, useMemo } from 'react';
import { BG, KANT, TEKST, STATUS, RADIUS, FONT } from './tokens';
import TomTilstand from './TomTilstand';

export default function DataTabel({
  kolonner = [],
  rækker = [],
  tomTekst = 'Ingen data',
  tomBesked = '',
  tomIkon = '○',
  pageSize = 10,
  style = {},
  loading = false,
}) {
  const [sorterKey,  setSorterKey]  = useState(null);
  const [sorterDir,  setSorterDir]  = useState('asc');
  const [filter,     setFilter]     = useState('');
  const [side,       setSide]       = useState(0);

  // OS-04: Normaliser kolonner — accepter både `key` og `nøgle` (legacy)
  // så OS-paneler der bruger nøgle: ikke krasher med undefined key-adgang.
  const normKoloaner = kolonner.map(kol => ({
    ...kol,
    key: kol.key ?? kol.nøgle ?? '',
  }));

  // Filtrering
  const filtreret = useMemo(() => {
    if (!filter.trim()) return rækker;
    const q = filter.toLowerCase();
    return rækker.filter(r =>
      Object.values(r).some(v => v != null && String(v).toLowerCase().includes(q))
    );
  }, [rækker, filter]);

  // Sortering
  const sorteret = useMemo(() => {
    if (!sorterKey) return filtreret;
    return [...filtreret].sort((a, b) => {
      const va = a[sorterKey] ?? '';
      const vb = b[sorterKey] ?? '';
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), 'da');
      return sorterDir === 'asc' ? cmp : -cmp;
    });
  }, [filtreret, sorterKey, sorterDir]);

  // Pagination
  const antalSider = pageSize > 0 ? Math.max(1, Math.ceil(sorteret.length / pageSize)) : 1;
  const visRækker  = pageSize > 0 ? sorteret.slice(side * pageSize, (side + 1) * pageSize) : sorteret;

  const skiftSort = key => {
    if (sorterKey === key) {
      setSorterDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSorterKey(key);
      setSorterDir('asc');
    }
    setSide(0);
  };

  const headerStyle = (kol) => ({
    padding:      '8px 12px',
    textAlign:    kol.align || 'left',
    fontSize:     11,
    fontWeight:   700,
    color:        TEKST.sekundær,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    cursor:       'pointer',
    userSelect:   'none',
    whiteSpace:   'nowrap',
    borderBottom: `1px solid ${KANT.standard}`,
    width:        kol.bredde || 'auto',
  });

  const cellStyle = (kol) => ({
    padding:   '9px 12px',
    textAlign: kol.align || 'left',
    fontSize:  13,
    color:     TEKST.primær,
    borderBottom: `1px solid ${KANT.fin}`,
    verticalAlign: 'middle',
  });

  return (
    // OS-02/OS-08: maxWidth:100% + minWidth:0 sikrer at tabellen aldrig
    // sprænger forælderens bredde. overflowX:auto på inner-div håndterer resten.
    <div style={{ maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', ...style }}>
      {/* Filter-søgefelt */}
      {rækker.length > 4 && (
        <div style={{ marginBottom: 10 }}>
          <input
            type="text"
            placeholder="Filter…"
            value={filter}
            onChange={e => { setFilter(e.target.value); setSide(0); }}
            style={{
              background:  'rgba(255,255,255,0.05)',
              border:      `1px solid ${KANT.standard}`,
              borderRadius: RADIUS.badge,
              color:       TEKST.primær,
              padding:     '5px 10px',
              fontSize:    12,
              width:       '100%',
              boxSizing:   'border-box',
              outline:     'none',
              fontFamily:  FONT.family,
            }}
          />
        </div>
      )}

      {/* Skeleton */}
      {loading ? (
        <div style={{ padding: '20px 12px', color: TEKST.sekundær, fontSize: 13 }}>
          Henter data…
        </div>
      ) : visRækker.length === 0 ? (
        <TomTilstand
          titel={tomTekst}
          besked={tomBesked}
          ikon={tomIkon}
          badge="LIVE"
          style={{ margin: '8px 0' }}
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT.family }}>
            <thead>
              <tr>
                {normKoloaner.map(kol => (
                  <th
                    key={kol.key}
                    style={headerStyle(kol)}
                    onClick={() => skiftSort(kol.key)}
                  >
                    {kol.label}
                    {sorterKey === kol.key ? (sorterDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visRækker.map((row, i) => (
                <tr
                  key={i}
                  style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(63,212,199,0.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'; }}
                >
                  {normKoloaner.map(kol => (
                    <td key={kol.key} style={cellStyle(kol)}>
                      {kol.render ? kol.render(row[kol.key], row) : (row[kol.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pageSize > 0 && antalSider > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, fontSize: 12, color: TEKST.sekundær }}>
          <span>{filtreret.length} rækker{filter ? ` (filter: "${filter}")` : ''}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => setSide(s => Math.max(0, s - 1))}
              disabled={side === 0}
              style={{ background: 'none', border: `1px solid ${KANT.standard}`, color: TEKST.sekundær, borderRadius: 5, padding: '2px 8px', cursor: side === 0 ? 'default' : 'pointer', opacity: side === 0 ? 0.4 : 1 }}
            >
              ←
            </button>
            <span style={{ padding: '2px 8px' }}>{side + 1} / {antalSider}</span>
            <button
              onClick={() => setSide(s => Math.min(antalSider - 1, s + 1))}
              disabled={side >= antalSider - 1}
              style={{ background: 'none', border: `1px solid ${KANT.standard}`, color: TEKST.sekundær, borderRadius: 5, padding: '2px 8px', cursor: side >= antalSider - 1 ? 'default' : 'pointer', opacity: side >= antalSider - 1 ? 0.4 : 1 }}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
